import React, {useCallback, useContext, useEffect, useState, useMemo} from 'react';
import dayjs from 'dayjs';
import { css } from "styled-components/macro"; //eslint-disable-line

import {DataGrid, GridCellModes} from '@mui/x-data-grid';
import {Alert, Autocomplete, Button, Box, Chip, Container, Card, CardContent, Dialog,
  DialogTitle, DialogContent, DialogActions, Grid, IconButton, InputAdornment,
  MenuItem, Stack, TextField, Typography, Tabs, Tab} from '@mui/material';
import {AccountBalance, AttachFile, Delete, Receipt, Schedule, Warning,
  CheckCircle, AttachMoney, MarkEmailRead, RemoveCircle} from '@mui/icons-material';

import AnimationRevealPage from "components/AnimationRevealPage.js";
import Header from "components/Header.js";
import {createInvoice, fetchInvoices, fetchInvoiceSummary, updateInvoice,
  payInvoice, linkDocumentToInvoice, unlinkDocumentFromInvoice,
  sendInvoiceReminder, markInvoicePaid, markInvoiceUnpaid} from "helpers/invoices.js";
import {createAndUploadDocument, downloadDocument, deleteDocument} from "helpers/documents.js";
import {fetchUsers} from "helpers/userManagement.js";
import AuthContext from 'contexts/AuthContext';

const DEFAULT_OFFSET = 0;
const DEFAULT_PAGE_SIZE = 10;
const PAGE_OPTIONS = [5, 10, 25, 50, 100];
const STATUS_OPTIONS = ["pending", "paid", "overdue"];
const PAYMENT_METHODS = ["cash", "check", "bank_transfer", "online", "other"];

export default function Invoices() {
  const { role, designation, user } = useContext(AuthContext);
  const inputFile = React.useRef(null);
  const billInputFile = React.useRef(null);

  const [cellModesModel, setCellModesModel] = useState({});
  const [invoiceDialogOpen, setInvoiceDialogOpen] = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [payingInvoice, setPayingInvoice] = useState(null);
  const [error, setError] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [invoiceSummary, setInvoiceSummary] = useState(null);
  const [rowCount, setRowCount] = useState(0);
  const [selectedFile, setSelectedFile] = useState(null);
  const [billTargetInvoice, setBillTargetInvoice] = useState(null);
  const [saving, setSaving] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [pgModel, setPgnModel] = useState({page: DEFAULT_OFFSET, pageSize: DEFAULT_PAGE_SIZE});
  const [members, setMembers] = useState([]);

  const hasPermission = useMemo(() =>
    role === "ADMIN" || role === "EXECUTIVE" || role === "MEMBER", [role]);
  // A Secretary-designated EXECUTIVE has handed Invoices off to a Treasurer -
  // an undesignated or President-designated EXECUTIVE keeps full access.
  const canEdit = useMemo(() =>
    role === "ADMIN" || (role === "EXECUTIVE" && designation !== "SECRETARY"), [role, designation]);

  const hasChanges = useMemo(() => {
    return invoices.some(inv => inv.isEdited && !inv.isNew)
  }, [invoices]);

  // Invoice handlers
  const handleCellClick = useCallback((params, event) => {
    if (!params.isEditable || (event.target?.nodeType === 1 && !event.currentTarget.contains(event.target))) {
      return;
    }
    setCellModesModel((prevModel) => {
      const newModel = Object.keys(prevModel).reduce((acc, id) => ({
        ...acc,
        [id]: Object.keys(prevModel[id]).reduce((acc2, field) => ({
          ...acc2,
          [field]: { mode: GridCellModes.View }
        }), {})
      }), {});
      return {
        ...newModel,
        [params.id]: {
          ...Object.keys(prevModel[params.id] || {}).reduce(
            (acc, field) => ({ ...acc, [field]: { mode: GridCellModes.View } }),
            {}
          ),
          [params.field]: { mode: GridCellModes.Edit }
        }
      };
    });
  }, []);

  const handleCellModesModelChange = useCallback((newModel) => {
    setCellModesModel(newModel);
  }, []);

  const handleRowUpdate = useCallback((updatedRow, originalRow) => {
    setInvoices(prev => prev.map(inv =>
      inv.id === updatedRow.id
        ? { ...updatedRow, isEdited: true, updated_at: new Date(), updated_by: user.name }
        : inv
    ));
    return updatedRow;
  }, [user.name]);

  const getNewId = useCallback((invs) => {
    if (!invs || invs.length === 0) return 1;
    return Math.max(...invs.map(inv => inv.id)) + 1;
  }, []);

  const handleAddInvoice = useCallback(() => {
    const newInvoice = {
      id: getNewId(invoices),
      member_id: "",
      member_name: "",
      member_email: "",
      title: "",
      description: "",
      amount: 0,
      due_date: new Date(),
      status: "pending",
      payment_date: null,
      payment_method: "",
      document_id: null,
      notes: "",
      created_by: user.name,
      created_at: new Date(),
      updated_by: user.name,
      updated_at: new Date(),
      isNew: true
    };
    setEditingInvoice(newInvoice);
    setSelectedFile(null);
    setInvoiceDialogOpen(true);
  }, [invoices, getNewId, user.name]);

  const handleSendReminder = useCallback(async (invoice) => {
    if (!window.confirm(`Send a payment reminder email to ${invoice.member_name}?`)) return;
    setError(null);
    try {
      await sendInvoiceReminder(invoice.id);
    } catch (err) {
      console.error('Error sending reminder:', err);
      setError('Failed to send reminder email. Please try again.');
    }
  }, []);

  const handlePayInvoice = useCallback((invoice) => {
    setPayingInvoice({
      ...invoice,
      payment_date: new Date(),
      payment_method: "bank_transfer",
      notes: ""
    });
    setPaymentDialogOpen(true);
  }, []);

  const loadInvoices = useCallback(async () => {
    setSummaryLoading(true);
    try {
      await fetchInvoiceSummary(setInvoiceSummary);
    } catch (err) {
      console.error('Error loading invoice summary:', err);
    } finally {
      setSummaryLoading(false);
    }

    setLoading(true);
    setError(null);
    try {
      const memberFilter = (role === "MEMBER") ? user.userRef : null;
      await fetchInvoices(pgModel.page, pgModel.pageSize,
        (list) => setInvoices((list || []).map(inv => ({
          ...inv,
          due_date: inv.due_date ? new Date(inv.due_date) : new Date(),
          payment_date: inv.payment_date ? new Date(inv.payment_date) : null
        }))),
        setRowCount, statusFilter, memberFilter);
    } catch (err) {
      console.error('Error loading invoices:', err);
      setError('Failed to load invoices. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [pgModel, statusFilter, role, user]);

  const handleMarkAsPaid = useCallback(async (invoice) => {
    if (!window.confirm(
      `Mark "${invoice.title}" as paid? This will add a matching credit entry to Finance.`
    )) return;
    setError(null);
    try {
      await markInvoicePaid(invoice.id);
      await loadInvoices();
    } catch (err) {
      console.error('Error marking invoice as paid:', err);
      setError('Failed to mark invoice as paid. Please try again.');
    }
  }, [loadInvoices]);

  const handleMarkAsUnpaid = useCallback(async (invoice) => {
    if (!window.confirm(
      `Mark "${invoice.title}" as unpaid? This will remove its matching credit entry from Finance.`
    )) return;
    setError(null);
    try {
      await markInvoiceUnpaid(invoice.id);
      await loadInvoices();
    } catch (err) {
      console.error('Error marking invoice as unpaid:', err);
      setError('Failed to mark invoice as unpaid. Please try again.');
    }
  }, [loadInvoices]);

  const handleSubmit = useCallback(async () => {
    setSaving(true);
    setError(null);
    try {
      const editedInvoices = invoices.filter(inv =>
        inv.isEdited && !inv.isNew && inv.amount > 0 && inv.member_id !== ""
      );
      const promises = editedInvoices.map(inv => {
        const request = {
          member_id: inv.member_id,
          member_name: inv.member_name,
          member_email: inv.member_email,
          title: inv.title,
          description: inv.description,
          amount: inv.amount,
          due_date: dayjs(inv.due_date).toISOString(),
          status: inv.status,
          payment_date: inv.payment_date ? dayjs(inv.payment_date).toISOString() : null,
          payment_method: inv.payment_method,
          notes: inv.notes
        };
        return updateInvoice(inv.id, request);
      });

      await Promise.all(promises);
      await loadInvoices();
    } catch (err) {
      console.error('Error saving invoices:', err);
      setError('Failed to save invoices. Please try again.');
    } finally {
      setSaving(false);
    }
  }, [invoices, loadInvoices]);

  const handleCancel = useCallback(() => {
    loadInvoices();
  }, [loadInvoices]);

  // Document handlers
  const handleFileSelected = useCallback((event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
    }
    event.target.value = '';
  }, []);

  const handleRemoveFile = useCallback(() => {
    setSelectedFile(null);
  }, []);

  const handleDownloadBill = useCallback(async (invoice) => {
    if (!invoice.document_id) return;
    try {
      await downloadDocument(invoice.document_id);
    } catch (err) {
      console.error('Error downloading bill:', err);
      setError('Failed to download bill. Please try again.');
    }
  }, []);

  const handleAttachBillClick = useCallback((invoice) => {
    setBillTargetInvoice(invoice);
    billInputFile.current.click();
  }, []);

  const handleBillFileSelected = useCallback(async (event) => {
    const file = event.target.files[0];
    event.target.value = '';
    if (!file || !billTargetInvoice) return;

    const invoice = billTargetInvoice;
    setBillTargetInvoice(null);
    setError(null);
    try {
      const createDocRequest = {
        filename: file.name,
        title: `${invoice.title} - Invoice`,
        description: `Invoice for: ${invoice.member_name}`,
        category: 'bills',
        file_size: file.size,
        mime_type: file.type
      };
      const createdDoc = await createAndUploadDocument(createDocRequest, file);
      await linkDocumentToInvoice(invoice.id, createdDoc.id);
      if (invoice.document_id) {
        await deleteDocument(invoice.document_id).catch(err =>
          console.error('Failed to clean up replaced bill:', err));
      }
      await loadInvoices();
    } catch (err) {
      console.error('Error attaching bill:', err);
      setError('Failed to attach bill. Please try again.');
    }
  }, [billTargetInvoice, loadInvoices]);

  const handleRemoveBill = useCallback(async (invoice) => {
    if (!invoice.document_id) return;
    if (!window.confirm('Remove the attached bill from this invoice?')) return;
    setError(null);
    try {
      await unlinkDocumentFromInvoice(invoice.id);
      await deleteDocument(invoice.document_id).catch(err =>
        console.error('Failed to delete removed bill:', err));
      await loadInvoices();
    } catch (err) {
      console.error('Error removing bill:', err);
      setError('Failed to remove bill. Please try again.');
    }
  }, [loadInvoices]);

  const handleDialogSubmit = useCallback(async () => {
    if (!editingInvoice?.member_id || !editingInvoice?.title || editingInvoice?.amount <= 0) {
      setError('Member ID, title, and valid amount are required.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      let documentId = editingInvoice.document_id;

      if (selectedFile) {
        const createDocRequest = {
          filename: selectedFile.name,
          title: `${editingInvoice.title} - Invoice`,
          description: `Invoice for: ${editingInvoice.member_name}`,
          category: 'bills',
          file_size: selectedFile.size,
          mime_type: selectedFile.type
        };

        const createdDoc = await createAndUploadDocument(createDocRequest, selectedFile);
        documentId = createdDoc.id;
      }

      const invoiceRequest = {
        member_id: editingInvoice.member_id,
        member_name: editingInvoice.member_name,
        member_email: editingInvoice.member_email,
        title: editingInvoice.title,
        description: editingInvoice.description,
        amount: editingInvoice.amount,
        due_date: dayjs(editingInvoice.due_date).toISOString(),
        document_id: documentId,
        notes: editingInvoice.notes
      };

      await createInvoice(invoiceRequest);

      setInvoiceDialogOpen(false);
      setEditingInvoice(null);
      setSelectedFile(null);
      await loadInvoices();
    } catch (err) {
      console.error('Error saving invoice:', err);
      setError('Failed to save invoice. Please try again.');
    } finally {
      setSaving(false);
    }
  }, [editingInvoice, selectedFile, loadInvoices]);

  const handlePaymentSubmit = useCallback(async () => {
    if (!payingInvoice?.payment_date || !payingInvoice?.payment_method) {
      setError('Payment date and method are required.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const paymentRequest = {
        payment_date: dayjs(payingInvoice.payment_date).toISOString(),
        payment_method: payingInvoice.payment_method,
        notes: payingInvoice.notes || ""
      };

      await payInvoice(payingInvoice.id, paymentRequest);

      setPaymentDialogOpen(false);
      setPayingInvoice(null);
      await loadInvoices();
    } catch (err) {
      console.error('Error processing payment:', err);
      setError('Failed to process payment. Please try again.');
    } finally {
      setSaving(false);
    }
  }, [payingInvoice, loadInvoices]);

  const handleDialogCancel = useCallback(() => {
    setInvoiceDialogOpen(false);
    setEditingInvoice(null);
    setSelectedFile(null);
    setPaymentDialogOpen(false);
    setPayingInvoice(null);
  }, []);

  const getStatusColor = (status) => {
    switch(status) {
      case 'paid': return 'success';
      case 'overdue': return 'error';
      case 'pending': return 'warning';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'paid': return <CheckCircle fontSize="small" />;
      case 'overdue': return <Warning fontSize="small" />;
      case 'pending': return <Schedule fontSize="small" />;
      default: return null;
    }
  };

  const columns = useMemo(() => [
    { field: 'due_date', headerName: 'Due Date', width: 150, editable: canEdit, type: 'date' },
    { field: 'member_id', headerName: 'Member ID', width: 120, editable: canEdit },
    { field: 'member_name', headerName: 'Member Name', minWidth: 150, flex: 1, editable: canEdit },
    { field: 'title', headerName: 'Title', minWidth: 150, flex: 1, editable: canEdit },
    { field: 'amount', headerName: 'Amount', width: 150, editable: canEdit, type: 'number' },
    { field: 'status', headerName: 'Status', width: 120, type: "singleSelect",
      valueOptions: STATUS_OPTIONS, editable: canEdit,
      renderCell: (params) => (
        <Chip label={params.value} color={getStatusColor(params.value)}
          icon={getStatusIcon(params.value)} size="small" />
      )
    },
    { field: 'description', headerName: 'Description', minWidth: 150, flex: 1, editable: canEdit },
    { field: 'document_id', headerName: 'Bill', width: 140,
      renderCell: (params) => (
        <Stack direction="row" spacing={0.5}>
          {params.row.document_id && (
            <IconButton size="small" color="primary" onClick={() => handleDownloadBill(params.row)}>
              <Receipt fontSize="small" />
            </IconButton>
          )}
          {canEdit && (
            <IconButton size="small" onClick={() => handleAttachBillClick(params.row)}
              title={params.row.document_id ? 'Replace bill' : 'Attach bill'}>
              <AttachFile fontSize="small" />
            </IconButton>
          )}
          {canEdit && params.row.document_id && (
            <IconButton size="small" color="error" onClick={() => handleRemoveBill(params.row)}
              title="Remove bill">
              <Delete fontSize="small" />
            </IconButton>
          )}
        </Stack>
      )
    },
    { field: 'actions', headerName: 'Actions', width: 190,
      renderCell: (params) => (
        <Stack direction="row" spacing={0.5}>
          {params.row.status !== 'paid' && (
            <IconButton size="small" color="success" title="Record a payment"
              onClick={() => handlePayInvoice(params.row)}>
              <AttachMoney fontSize="small" />
            </IconButton>
          )}
          {params.row.status !== 'paid' && canEdit && (
            <IconButton size="small" color="primary" title="Send payment reminder"
              onClick={() => handleSendReminder(params.row)}>
              <MarkEmailRead fontSize="small" />
            </IconButton>
          )}
          {params.row.status !== 'paid' && canEdit && (
            <IconButton size="small" color="success" title="Mark as paid (auto-adds a Finance credit entry)"
              onClick={() => handleMarkAsPaid(params.row)}>
              <CheckCircle fontSize="small" />
            </IconButton>
          )}
          {params.row.status === 'paid' && canEdit && (
            <IconButton size="small" color="warning" title="Mark as unpaid (removes the linked Finance entry)"
              onClick={() => handleMarkAsUnpaid(params.row)}>
              <RemoveCircle fontSize="small" />
            </IconButton>
          )}
        </Stack>
      )
    },
    { field: 'payment_date', headerName: 'Payment Date', width: 150, type: 'date' },
    { field: 'payment_method', headerName: 'Payment Method', width: 150 },
    { field: 'created_by', headerName: 'Added By', minWidth: 100, flex: 1 },
    { field: 'created_at', headerName: 'Added At', width: 150, type: 'date',
      valueGetter: (params) => params.row?.created_at ? new Date(params.row.created_at) : new Date() },
  ], [handleDownloadBill, handleAttachBillClick, handleRemoveBill, handlePayInvoice,
    handleSendReminder, handleMarkAsPaid, handleMarkAsUnpaid, canEdit]);

  useEffect(() => {
    if (hasPermission) {
      loadInvoices();
    }
  }, [loadInvoices, hasPermission]);

  useEffect(() => {
    if (canEdit) {
      fetchUsers().then(res => setMembers(Array.isArray(res) ? res : []));
    }
  }, [canEdit]);

  if (!hasPermission) {
    return (
      <AnimationRevealPage>
        <Header />
        <Container>
          <Alert severity="error">Permission denied</Alert>
        </Container>
      </AnimationRevealPage>
    );
  }

  return (
    <AnimationRevealPage>
      <Header />
      <Container style={{padding: 15, marginTop: 40, maxWidth: "100%"}}>
        {error && (
          <Alert severity="error" style={{marginBottom: 20}}>
            {error}
          </Alert>
        )}
        <input type="file" ref={inputFile} accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png"
          onChange={handleFileSelected} style={{display: 'none'}} />
        <input type="file" ref={billInputFile} accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png"
          onChange={handleBillFileSelected} style={{display: 'none'}} />

        {/* Summary Cards */}
        {!summaryLoading && invoiceSummary && (
          <Grid container spacing={2} style={{ marginBottom: 50 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card elevation={2}>
                <CardContent>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography color="textSecondary" gutterBottom variant="body2">
                        Total Amount
                      </Typography>
                      <Typography variant="h5">
                        ₹{invoiceSummary.total_amount?.toLocaleString('en-US', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        }) || '0.00'}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {invoiceSummary.total_invoices} invoices
                      </Typography>
                    </Box>
                    <AccountBalance color="primary" fontSize="large" />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card elevation={2}>
                <CardContent>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography color="textSecondary" gutterBottom variant="body2">
                        Paid
                      </Typography>
                      <Typography variant="h5" color="success.main">
                        ₹{invoiceSummary.paid_amount?.toLocaleString('en-US', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        }) || '0.00'}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {invoiceSummary.paid_count} invoices
                      </Typography>
                    </Box>
                    <CheckCircle color="success" fontSize="large" />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card elevation={2}>
                <CardContent>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography color="textSecondary" gutterBottom variant="body2">
                        Pending
                      </Typography>
                      <Typography variant="h5" color="warning.main">
                        ₹{invoiceSummary.pending_amount?.toLocaleString('en-US', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        }) || '0.00'}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {invoiceSummary.pending_count} invoices
                      </Typography>
                    </Box>
                    <Schedule color="warning" fontSize="large" />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card elevation={2}>
                <CardContent>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography color="textSecondary" gutterBottom variant="body2">
                        Overdue
                      </Typography>
                      <Typography variant="h5" color="error.main">
                        ₹{invoiceSummary.overdue_amount?.toLocaleString('en-US', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        }) || '0.00'}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {invoiceSummary.overdue_count} invoices
                      </Typography>
                    </Box>
                    <Warning color="error" fontSize="large" />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* Status Filter Tabs */}
        <Box mb={3}>
          <Tabs value={statusFilter} onChange={(e, newValue) => setStatusFilter(newValue)}>
            <Tab label="All" value="" />
            <Tab label="Pending" value="pending" />
            <Tab label="Paid" value="paid" />
            <Tab label="Overdue" value="overdue" />
          </Tabs>
        </Box>

        {/* Add/Edit Invoice Dialog */}
        <Dialog open={invoiceDialogOpen} onClose={handleDialogCancel} maxWidth="sm" fullWidth>
          <DialogTitle>Add New Invoice</DialogTitle>
          <DialogContent>
            <Autocomplete
              options={members}
              getOptionLabel={(option) => option.name ? `${option.name} (${option.email})` : ''}
              isOptionEqualToValue={(option, value) => option.userRef === value.userRef}
              value={members.find(m => m.userRef === editingInvoice?.member_id) || null}
              onChange={(e, newValue) => setEditingInvoice(prev => ({
                ...prev,
                member_id: newValue?.userRef || '',
                member_name: newValue?.name || '',
                member_email: newValue?.email || ''
              }))}
              renderInput={(params) => (
                <TextField {...params} margin="dense" label="Member" variant="outlined" required fullWidth />
              )}
            />
            <TextField margin="dense" label="Due Date" type="date" fullWidth variant="outlined"
              value={editingInvoice?.due_date ? dayjs(editingInvoice.due_date).format('YYYY-MM-DD') : ''}
              onChange={(e) => setEditingInvoice(prev => ({...prev, due_date: new Date(e.target.value)}))}
              required InputLabelProps={{ shrink: true }}
            />
            <TextField margin="dense" label="Title" variant="outlined"
              value={editingInvoice?.title || ''} required fullWidth
              onChange={(e) => setEditingInvoice(prev => ({...prev, title: e.target.value}))}
            />
            <TextField margin="dense" label="Amount" type="number" fullWidth
              variant="outlined" value={editingInvoice?.amount}
              onChange={(e) => setEditingInvoice(prev => ({...prev, amount: parseFloat(e.target.value) || 0}))}
              required InputProps={{startAdornment: <InputAdornment position="start">₹</InputAdornment>}}
            />
            <TextField margin="dense" label="Description" fullWidth variant="outlined" multiline
              rows={3} value={editingInvoice?.description || ''}
              onChange={(e) => setEditingInvoice(prev => ({...prev, description: e.target.value}))}
            />
            <TextField margin="dense" label="Notes" fullWidth variant="outlined" multiline
              rows={2} value={editingInvoice?.notes || ''}
              onChange={(e) => setEditingInvoice(prev => ({...prev, notes: e.target.value}))}
            />
            <Box mt={2}>
              <Typography variant="subtitle2" gutterBottom>
                Attach Document (Optional)
              </Typography>
              {!selectedFile ? (
                <Button variant="outlined" startIcon={<AttachFile />}
                  onClick={() => inputFile.current.click()} fullWidth >
                  Upload Document
                </Button>
              ) : (
                <Box display="flex" alignItems="center" justifyContent="space-between"
                  p={2} border={1} borderColor="primary.main" borderRadius={1} >
                  <Box display="flex" alignItems="center" gap={1}>
                    <Receipt color="primary" />
                    <Box>
                      <Typography variant="body2">
                        {selectedFile.name}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                      </Typography>
                    </Box>
                  </Box>
                  <IconButton size="small" color="error" onClick={handleRemoveFile} >
                    <Delete />
                  </IconButton>
                </Box>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDialogCancel} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleDialogSubmit} variant="contained" disabled={saving} >
              {saving ? 'Creating...' : 'Create Invoice'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Payment Dialog */}
        <Dialog open={paymentDialogOpen} onClose={handleDialogCancel} maxWidth="sm" fullWidth>
          <DialogTitle>Record Payment</DialogTitle>
          <DialogContent>
            <Alert severity="info" style={{marginBottom: 16}}>
              <Typography variant="body2">
                <strong>Invoice:</strong> {payingInvoice?.title}
              </Typography>
              <Typography variant="body2">
                <strong>Member:</strong> {payingInvoice?.member_name}
              </Typography>
              <Typography variant="body2">
                <strong>Amount:</strong> ₹{payingInvoice?.amount?.toLocaleString()}
              </Typography>
            </Alert>
            <TextField margin="dense" label="Payment Date" type="date" fullWidth variant="outlined"
              value={payingInvoice?.payment_date ? dayjs(payingInvoice.payment_date).format('YYYY-MM-DD') : ''}
              onChange={(e) => setPayingInvoice(prev => ({...prev, payment_date: new Date(e.target.value)}))}
              required InputLabelProps={{ shrink: true }}
            />
            <TextField select margin="dense" label="Payment Method" fullWidth required
              variant="outlined" value={payingInvoice?.payment_method || 'bank_transfer'}
              onChange={(e) => setPayingInvoice(prev => ({...prev, payment_method: e.target.value}))} >
              {PAYMENT_METHODS.map((method) => (
                <MenuItem key={method} value={method}>
                  {method.charAt(0).toUpperCase() + method.slice(1).replace('_', ' ')}
                </MenuItem>
              ))}
            </TextField>
            <TextField margin="dense" label="Notes" fullWidth variant="outlined" multiline
              rows={3} value={payingInvoice?.notes || ''}
              onChange={(e) => setPayingInvoice(prev => ({...prev, notes: e.target.value}))}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDialogCancel} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handlePaymentSubmit} variant="contained" color="success" disabled={saving} >
              {saving ? 'Processing...' : 'Record Payment'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Invoices Table */}
        <DataGrid autoHeight loading={loading} style={{marginBottom: 20}}
          sx={{
            "& .MuiDataGrid-columnHeaderTitle": {
              color: "#575757", whiteSpace: "normal",
              lineHeight: "normal", fontWeight: 600,
            },
            "& .MuiDataGrid-columnHeaders": {
              maxHeight: "168px !important"
            },
            "& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus": {
              outline: "none"
            }
          }}
          initialState={{
            sorting: { sortModel: [{ field: 'due_date', sort: 'desc' }] },
            columns: {columnVisibilityModel: {
              "payment_date": false, "payment_method": false,
              "created_by": false, "created_at": false
            }},
          }}
          rows={invoices}
          columns={columns}
          cellModesModel={cellModesModel}
          pageSizeOptions={PAGE_OPTIONS}
          paginationMode="server"
          rowCount={rowCount}
          paginationModel={pgModel}
          onPaginationModelChange={setPgnModel}
          onCellModesModelChange={handleCellModesModelChange}
          onCellClick={handleCellClick}
          disableRowSelectionOnClick
          processRowUpdate={handleRowUpdate}
        />
        <Stack direction="row" spacing={1}>
          <Button color="primary" variant="contained"
            onClick={handleAddInvoice} disabled={saving} >+ Add Invoice</Button>
          <Button
            color="success"
            style={{ marginLeft: 'auto' }}
            variant="outlined"
            onClick={handleCancel}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            color="success"
            variant="contained"
            onClick={handleSubmit}
            disabled={saving || !hasChanges}
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </Stack>
      </Container>
    </AnimationRevealPage>
  );
}
